import Button from "components/button";
import Modal from "components/modal";
import React, { useEffect, useState } from "react";
import bluelock from "assets/icons/blue-lock.svg";
import { useWalletSettings } from "hooks/useWalletSettings";
import QRCode from "qrcode";
import { toast } from "sonner";

const ScanQRcode: React.FC = () => {
  const {
    navigate,
    handleSendVerificationCode,
    qrCode,
    isSendingOtp,
    currentsetup,
    getTotpSecret,
  } = useWalletSettings();
  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState(200);

  // Set QR code size based on screen width
  useEffect(() => {
    const updateQrSize = () => {
      const width = window.innerWidth;
      if (width < 350) {
        setQrSize(180);
      } else if (width < 640) {
        setQrSize(200);
      } else {
        setQrSize(240);
      }
    };

    // Set initial size
    updateQrSize();

    // Update size on resize
    window.addEventListener("resize", updateQrSize);
    return () => window.removeEventListener("resize", updateQrSize);
  }, []);

  // Generate QR code when qrCode is available
  useEffect(() => {
    if (qrCode && currentsetup === "authenticator") {
      const canvas = document.getElementById("qrCodeCanvas");
      if (canvas && typeof qrCode === "string") {
        QRCode.toCanvas(canvas, qrCode, { width: qrSize }, (error) => {
          if (error) {
            console.error("QR code generation failed:", error);
            toast.error("Failed to generate QR code. Please try again.");
          }
        });
      } else {
        toast.error("Invalid QR code data.");
      }
    }
  }, [qrCode, currentsetup, qrSize]);

  // Handle copying TOTP secret
  const handleCopySecret = () => {
    const secret = getTotpSecret();
    if (secret) {
      navigator.clipboard
        .writeText(secret)
        .then(() => {
          setCopied(true);
          toast.success("Secret key copied to clipboard!");
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          toast.error("Failed to copy secret key.");
        });
    } else {
      toast.error("No secret key available.");
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={() => navigate("/wallet/settings/two-factor-setup")}
      className="fixed"
    >
      <div className="flex flex-col justify-center p-4 md:p-6 w-full max-w-md min-h-[320px] gap-4 md:gap-6 rounded-lg md:rounded-2xl bg-white">
        <div className="h-12 w-12 border border-[#C2E2FF80] mx-auto rounded-full flex items-center justify-center">
          <img src={bluelock} alt="Lock icon" className="h-8 w-8" />
        </div>

        <div className="text-center text-slate-600">
          <h3 className="text-xl md:text-2xl font-bold">
            {currentsetup === "authenticator"
              ? "Set Up Authenticator App"
              : "Enter Manual Code"}
          </h3>
          <p className="text-center text-xs md:text-sm mt-1 px-2">
            {currentsetup === "authenticator"
              ? "Scan the QR code with your authenticator app (e.g., Google Authenticator). If you don't have an authenticator app, install one now."
              : "Enter the code below into your authenticator app to link it to your account."}
          </p>
        </div>

        <div className="py-2 flex flex-col items-center">
          <div className="flex flex-col items-center text-gray-900 rounded-lg gap-2 w-full">
            {currentsetup === "authenticator" && qrCode && (
              <div className="flex justify-center items-center mb-4 p-2 bg-white border border-gray-200 rounded-lg">
                <canvas id="qrCodeCanvas" />
              </div>
            )}

            {currentsetup === "code-authenticator" && (
              <div className="flex flex-col items-center gap-2 w-full">
                <div className="w-full overflow-x-auto">
                  <p className="text-sm md:text-base font-mono bg-gray-100 p-2 rounded text-center break-all">
                    {getTotpSecret() || "No secret available"}
                  </p>
                </div>
                <Button
                  label={copied ? "Copied!" : "Copy Code"}
                  onClick={handleCopySecret}
                  variant="secondary"
                  size="small"
                  disabled={!getTotpSecret()}
                />
              </div>
            )}

            {!qrCode && currentsetup === "authenticator" && (
              <p className="text-red-500">Loading QR code...</p>
            )}

            {currentsetup === "authenticator" ? (
              <p
                onClick={() =>
                  navigate(
                    "/wallet/settings/two-factor-setup/provide-qr-code?currentSetup=code-authenticator"
                  )
                }
                className="block text-xs md:text-sm font-medium text-blue-500 cursor-pointer hover:underline mt-1"
              >
                Can't scan QR code?
              </p>
            ) : (
              <p
                onClick={() =>
                  navigate(
                    "/wallet/settings/two-factor-setup/provide-qr-code?currentSetup=authenticator"
                  )
                }
                className="block text-xs md:text-sm font-medium text-blue-500 cursor-pointer hover:underline mt-1"
              >
                Try scanning QR code again
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-1 md:mt-2">
          {!qrCode && currentsetup === "authenticator" && (
            <Button
              label={isSendingOtp ? "Generating..." : "Generate QR Code"}
              onClick={handleSendVerificationCode}
              variant="primary"
              fullWidth
              size="large"
              disabled={isSendingOtp}
            />
          )}
          {qrCode && (
            <Button
              label="Next"
              onClick={() =>
                navigate(
                  "/wallet/settings/two-factor-setup/provide-otp?currentSetup=authenticator"
                )
              }
              variant="primary"
              fullWidth
              size="large"
              disabled={isSendingOtp}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ScanQRcode;
